import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { buildProviders } from '@kalidea/kaligraphi';
import { AbstractControl, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
    CritereRechercheNumeroPieceDtoInput,
    MutationCheckNumeroPieceForJournalArgs,
    MutationGetDernierCompteEcritureNumeroArgs
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { Store } from '@ngxs/store';
import { UserState } from 'src/app/_user/store/user/user.state';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { isNullOrEmpty } from 'src/app/utils/string.utils';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';

const PART_NUMBER_MAX_LENGTH = 20;
const PART_NUMBER_GREATER_THAN = 0;

@Component({
    selector: 'app-part-number',
    templateUrl: './part-number.component.html',
    styleUrls: ['./part-number.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(PartNumberComponent),
    standalone: false
})
export class PartNumberComponent extends CustomFormControlAccess implements OnInit {

    @Input() validatorsList: ValidatorFn[] = [];

    @Input() label = 'Numéro de pièce';

    @Input() ignoredPartNumber: string = null;

    readonly errorsMessages = {
        invalidcriteria: 'Vous devez d\'abord sélectionner {value}',
    };

    lastPartNumber: string = null;

    control: UntypedFormControl;

    private subscriptionList: Subscription[] = [];

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    // <editor-fold defaultstate="collapsed" desc="Loading">
    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    // </editor-fold>

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    // <editor-fold defaultstate="collapsed" desc="Criteria">
    get criteria$(): Observable<CritereRechercheNumeroPieceDtoInput> {
        return this._criteria.asObservable();
    }

    _criteria = new BehaviorSubject<CritereRechercheNumeroPieceDtoInput>(null);

    get criteria(): CritereRechercheNumeroPieceDtoInput {
        return this._criteria.value;
    }

    // </editor-fold>

    @Input() set criteria(criteria: CritereRechercheNumeroPieceDtoInput) {
        this._criteria.next(criteria);
    }

    // <editor-fold defaultstate="collapsed" desc="PartNumber">
    private _partNumber = new BehaviorSubject<string>(null);

    get partNumber(): string {
        return this._partNumber.getValue();
    }

    // </editor-fold>

    get partNumber$(): Observable<string> {
        return this._partNumber.asObservable();
    }

    writeValue(value: string): void {
        value = value === null ? null : '' + value;
        this._partNumber.next(value);
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.control.valid ? null : {invalidpartnumber: true});
    }

    refresh(): void {
        this.checkCriteria(this.criteria);
        this.watch$ = this.getLastPartNumber().pipe(
            tap(() => this._partNumber.next(null))
        );
    }

    initSubscription(): void {
        this.subscriptionList.forEach(s => s?.unsubscribe());

        this.subscriptionList.push(
            this.partNumber$.pipe(
                this.untilDestroyed,
                tap(value => {
                    this.loading = true;
                    this.updateForm(value);
                    this.checkCriteria(this.criteria);
                }),
                switchMap((partNumber) => this.checkPartNumber('' + partNumber).pipe(map(() => partNumber))),
                tap(() => {
                    this.notifyUpdate(this.control.value);
                    this.loading = false;
                }),
            ).subscribe()
        );

        this.subscriptionList.push(
            this.control.valueChanges.pipe(
                this.untilDestroyed,
                tap(() => {
                    this.loading = true;
                    this.checkCriteria(this.criteria);
                }),
                switchMap((partNumber) => this.checkPartNumber('' + partNumber)),
                tap(() => {
                    this.notifyUpdate('' + this.control.value);
                    this.loading = false;
                }),
            ).subscribe()
        );
    }

    private updateForm(value: string): void {
        if (!this.lastPartNumber) {
            return;
        }
        this.control.patchValue(value || (+this.lastPartNumber + 1), {emitEvent: false});
    }

    private getLastPartNumber(): Observable<string> {
        if (this.control.hasError('invalidcriteria')) {
            return of(null);
        }

        this.loading = true;
        return this.graphqlService.mutateOne<'getDernierCompteEcritureNumero', MutationGetDernierCompteEcritureNumeroArgs>({
            mutation: gql`
                mutation getDernierCompteEcritureNumero($contexte: ContextInfosInput, $critere: CritereRechercheNumeroPieceDTOInput){
                    getDernierCompteEcritureNumero(
                        contexte: $contexte,
                        critere: $critere
                    )
                }
            `,
            variables: {
                contexte: this.store.selectSnapshot(UserState.userContext),
                critere: this.criteria
            }
        }).pipe(
            tap(getDernierCompteEcritureNumero => {
                this.lastPartNumber = getDernierCompteEcritureNumero || '0';
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private checkCriteria(criteria: CritereRechercheNumeroPieceDtoInput): void {
        const {IdJournal, DateEcriture} = criteria || {};
        const valueError: string[] = [];
        const errors = this.control.errors || {};
        // on supprime invalidcriteria qui n'est pas supprimer automatiquement
        delete errors.invalidcriteria;

        this.control.setErrors(null);

        if (!IdJournal) {
            valueError.push('un journal');
        }

        if (!DateEcriture) {
            valueError.push('une date d\'exercice');
        }

        if (!isNullOrEmpty(valueError)) {
            this.lastPartNumber = null;
            this.control.patchValue(null, {emitEvent: false});
            this.notifyUpdate(null);
            errors.invalidcriteria = {value: valueError.join(' et ')};
        }

        this.control.setErrors(arrayEmpty(Object.values(errors)) ? null : errors);
    }

    private checkPartNumber(partNumber: string): Observable<boolean> {
        if (this.control.invalid) {
            return of(null);
        }
        this.loading = true;
        return this.graphqlService.mutateOne<'CheckNumeroPieceForJournal', MutationCheckNumeroPieceForJournalArgs>({
            mutation: gql`
                mutation CheckNumeroPieceForJournal($contexte: ContextInfosInput,
                    $IdJournal: Float,
                    $DateEcriture: DateTime,
                    $NumeroPiece: String,
                    $idActivite: Float){
                    CheckNumeroPieceForJournal(
                        contexte: $contexte,
                        IdJournal: $IdJournal,
                        DateEcriture: $DateEcriture,
                        NumeroPiece: $NumeroPiece,
                        idActivite: $idActivite
                    )
                }`,
            variables: {
                contexte: this.store.selectSnapshot(UserState.userContext),
                IdJournal: this.criteria.IdJournal,
                DateEcriture: this.criteria.DateEcriture,
                NumeroPiece: partNumber,
                idActivite: this.criteria.IdActivite || null,
            }
        }).pipe(
            tap(CheckNumeroPieceForJournal => {
                if (CheckNumeroPieceForJournal && !this.ignoredPartNumber) {
                    this.control.setErrors({invalidpartnumber: true});
                }
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    ngOnInit(): void {
        this.control = new UntypedFormControl(null, {
            validators: [
                ...(isNullOrEmpty(this.validatorsList) ? [] : this.validatorsList),
                FormValidator.isNotNull,
                FormValidator.greaterThan(PART_NUMBER_GREATER_THAN),
                Validators.maxLength(PART_NUMBER_MAX_LENGTH)
            ],
            updateOn: 'blur'
        });

        this.watch$ = this.criteria$.pipe(
            tap((criteria) => this.checkCriteria(criteria)),
            switchMap(() => this.getLastPartNumber()),
            tap(() => this.initSubscription())
        );
    }
}
